import React, { createContext, useCallback, useContext, useMemo, useState } from "react";
import { Member } from "src/patents/patents";

export type RowObject = Record<number|string, string>;

/* TODO
 - identify number and country columns
 - standardize patent numbers
 - import from EPO
*/

//type EpoData = Record<string, (Member | null)[]>;
type EpoData = ((Member & { inventors?: string[], applicants?: string[] }) | null)[];

export const ExcelCopyContext = createContext({
  rawText: "" as string,
  setRawText: ((_rawText: string) => {}) as React.Dispatch<React.SetStateAction<string>>,
  rows: [] as RowObject[],
  header: [] as string[],
  standardizedNumbers: [] as string[],
  setStandardizedNumbers: ((_standardizedNumbers: string[]) => {}) as React.Dispatch<React.SetStateAction<string[]>>,
  epoData: [] as EpoData,
  setEpoData: ((_epoData: EpoData) => {}) as React.Dispatch<React.SetStateAction<EpoData>>,
  withHeader: true as boolean,
  setWithHeader: ((_withHeader: boolean) => {}) as React.Dispatch<React.SetStateAction<boolean>>,
})

export function ExcelCopyProvider({children}: {children: React.ReactNode}): JSX.Element {
  const [rawText, setRawText] = useState<string>("");
  //const [rows, _setRows] = useState<RowObject[]>([]);
  const [withHeader, setWithHeader] = useState<boolean>(true);

  const [standardizedNumbers, setStandardizedNumbers] = useState<string[]>([]);
  const [epoData, setEpoData] = useState([]);

  const [rows, header] = useMemo(() => {
  //useEffect(() => {
    const text = rawText;
    if (!text) return [[], []]

    // Normalize line endings, split into lines
    const lines = text.split(/\r\n|\n|\r/);

    let header = lines[0].split("\t");
    if (!withHeader) {
      header = header.map((_, idx) => '' + idx);
    }

    //let getHeader = (id: number): string => '' + id;
    //if (withHeader) {
    //  const header = lines[0].split("\t")
    //  getHeader = (id: number): string => header[id] || ('' + id);
    //}

    // Convert each line into an object: {0: cell0, 1: cell1, ...}
    const parsed = lines
      .slice(withHeader ? 1 : 0)
      // remove a trailing empty line that often appears on clipboard copies
      .filter((ln, idx) => {
        // keep line if it has any non-tab non-space char, or it's not the last line
        if (ln.trim() !== "") return true;
        return idx !== lines.length - 1;
      })
      .map((line) => {
        // Excel copies are tab-delimited
        const cells = line.split("\t");
        const obj: RowObject = {};
        for (let i = 0; i < cells.length; i++) {
          //obj[getHeader(i)] = cells[i];
          obj[header[i]] = cells[i];
        }
        return obj;
      });

    //setRows(parsed);

    //return () => {}

    return [parsed, header];

  }, [rawText, withHeader]);

  const value = {
    rows,
    header,
    rawText,
    setRawText,
    withHeader,
    setWithHeader,
    standardizedNumbers,
    setStandardizedNumbers,
    epoData,
    setEpoData,
  }

  return (
    <ExcelCopyContext.Provider value={value}>
      {children}
    </ExcelCopyContext.Provider>
  )
}

export default function ExcelCopy(): JSX.Element {
  const {rawText, setRawText} = React.useContext(ExcelCopyContext);

  const handlePaste = useCallback(
    (e: React.ClipboardEvent<HTMLTextAreaElement | HTMLDivElement>) => {
      e.preventDefault();
      const text = e.clipboardData.getData("text/plain");
      setRawText(text);
      //setRows(parseExcelClipboard(text, withHeader));
    },
    //[parseExcelClipboard, withHeader]
    [setRawText]
  );

  const readFromClipboard = useCallback(async () => {
    try {
      const text = await navigator.clipboard.readText();
      setRawText(text);
      //setRows(parseExcelClipboard(text, withHeader));
    } catch (err) {
      // ignore or surface minimal error
      console.error("Clipboard read failed", err);
    }
  }, [setRawText]);

  return (
    <div className="space-y-2">
      <div>
        <button onClick={readFromClipboard} className="underline text-pcx-600 hover:text-pcx-800 mr-1">
          Read from clipboard
        </button>
        <span>or paste directly into the box below (Ctrl/Cmd+V).</span>
      </div>

      <div
        onPaste={handlePaste}
        contentEditable
        suppressContentEditableWarning
        className="border whitespace-pre-wrap p-4 min-h-32 rounded shadow-inner focus:ring-0 focus:outline-none focus:border-pcx-400"
        aria-label="Paste excel text here"
      >
        {rawText}
      </div>

      {/* 
      <div style={{ marginBottom: 12 }}>
        <strong>Parsed rows:</strong> {rows.length}
      </div>

      <pre
        style={{
          maxHeight: 300,
          overflow: "auto",
          background: "#f7f7f7",
          padding: 8,
          borderRadius: 4,
          border: "1px solid #eee",
        }}
      >
        {JSON.stringify(rows, null, 2)}
      </pre> */}
    </div>
  )
}

// {"patentFamilyId":-1,"realm":"Lego","title":"Cylinder inner surface i.e. sliding surface, fine-processing method for borehole in workpiece i.e. engine block, involves adapting processing parameter of plateau honing stage to surface structure of intermediate-honed borehole",
// "countryCode":"DE","applicationNumber":"DE102007017977A","applicationDate":"2007-04-05","publicationNumber":"DE102007017977A1",
// "publicationDate":"2008-06-05","patentNumber":"DE102007017977B4","patentDate":"2011-11-24","familyMemberStatus":"granted",
// "patentOfficeLink":"https://worldwide.espacenet.com/patent/search/family/39339035/publication/DE102007017977A1?q=DE102007017977A1",
// "ipType":"patent","internalReference":"P 47151DE","pctRouteFiling":false,"unitaryPatent":false}

const epoDataFields = [
  "title",
  "applicationNumber",
  "applicationDate",
  "publicationNumber",
  "publicationDate",
  "patentNumber",
  "patentDate",
  "familyMemberStatus",
  "patentOfficeLink",
  "ipType",
  "pctRouteFiling",
  "unitaryPatent",
  "validated",
]

const epoDataSeqFields = [
  "inventors",
  "applicants",
]


export function ParsedRows({id}: {id?: string}): JSX.Element {
  const {rows, header, standardizedNumbers, epoData} = useContext(ExcelCopyContext);

  const hasStandardized = standardizedNumbers.length === rows.length;
  const hasEpo = epoData.length === rows.length;

  return (
    <table id={id}>
      <thead>
        <tr className="pc-header-row">
          {header.map(col => (
            <th key={col} className="pc-header-cell">
              {col}
            </th>
          ))}
          {hasStandardized && <th className="pc-header-cell">EPO Number</th>}
          {hasEpo &&
            [...epoDataFields, ...epoDataSeqFields].map(field => (
              <th className="pc-header-cell" key={field}>
                {field}
              </th>
            ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, rowIndex) => (
          <tr key={rowIndex} className="hover:bg-pcx-200">
            {header.map(col => (
              <td key={col} className="pc-cell py-0 text-sm whitespace-nowrap">
                {row[col]}
              </td>
            ))}
            {hasStandardized && <td className="pc-cell py-0 text-sm">{standardizedNumbers[rowIndex]}</td>}
            {hasEpo &&
              [...epoDataFields.map(field => {
                let value = epoData[rowIndex]?.[field];
                // if value is boolean, show as TRUE/FALSE
                if (typeof value === 'boolean') {
                  value = value ? 'TRUE' : 'FALSE';
                }
                return <td className="pc-cell py-0 text-sm whitespace-nowrap" key={field}>
                  {value ?? ''}
                </td>
              }),
              ...epoDataSeqFields.map(field => (
                <td className="pc-cell py-0 text-sm whitespace-nowrap" key={field}>
                  {(epoData[rowIndex]?.[field] as string[] | undefined)?.join('; ') ?? ''}
                </td>
              ))
              ]}
          </tr>
        ))}
      </tbody>
      <tfoot>
        <tr className="pc-footer-row">
          {header.map(h => (
            <td key={h}></td>
          ))}
          {hasStandardized && <td />}
          {hasEpo && [...epoDataFields, ...epoDataSeqFields].map(field => <td key={field}></td>)}
        </tr>
      </tfoot>
    </table>
  )
}